import { CommonModule } from '@angular/common';
import { ModuleWithProviders, NgModule, Provider } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { FORMLY_CONFIG, FormlyModule as NgxFormly } from '@ngx-formly/core';
import { FormlySelectModule } from '@ngx-formly/core/select';
import { QuillModule } from 'ngx-quill';
import { AutoCompleteModule } from '@openng/optimus-ui/autocomplete';
import { BadgeModule } from '@openng/optimus-ui/badge';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CheckboxModule } from '@openng/optimus-ui/checkbox';
import { ColorPickerModule } from '@openng/optimus-ui/colorpicker';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { InputGroup } from '@openng/optimus-ui/inputgroup';
import { InputGroupAddon } from '@openng/optimus-ui/inputgroupaddon';
import { InputMaskModule } from '@openng/optimus-ui/inputmask';
import { InputNumberModule } from '@openng/optimus-ui/inputnumber';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { Select } from '@openng/optimus-ui/select';
import { TableModule } from '@openng/optimus-ui/table';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { ToggleSwitchModule } from '@openng/optimus-ui/toggleswitch';
import { TabsQueryDirective } from '~ngx-shared/directives';
import { SpinnerComponent } from '~ngx-shared/layout';
import { FileComponent } from './components/file/file.component';
import { FormComponent } from './components/form/form.component';
import { HtmlViewerComponent } from './components/html-viewer/html-viewer.component';
import { PickListComponent } from './components/pick-list/pick-list.component';
import { registerFormlyTranslocoExtension } from './extensions/formly-transloco.extension';
import { AutoCompleteSelectTypeComponent } from './types/auto-complete-select-type/auto-complete-select-type.component';
import { AutoCompleteTypeComponent } from './types/auto-complete-type/auto-complete-type.component';
import { CheckboxTypeComponent } from './types/checkbox-type/checkbox-type.component';
import { ColorTypeComponent } from './types/color-type/color-type.component';
import { DateTypeComponent } from './types/date-type/date-type.component';
import { EditorTypeComponent } from './types/editor-type/editor-type.component';
import { FileTypeComponent } from './types/file-type/file-type.component';
import { InputMaskTypeComponent } from './types/input-mask-type/input-mask-type.component';
import { LabelTypeComponent } from './types/label-type/label-type.component';
import { MultiSelectTypeComponent } from './types/multi-select-type/multi-select-type.component';
import { NumberTypeComponent } from './types/number-type/number-type.component';
import { PickListTypeComponent } from './types/pick-list-type/pick-list-type.component';
import { RadioTypeComponent } from './types/radio-type/radio-type.component';
import { RepeatTypeComponent } from './types/repeat-type/repeat-type.component';
import { SelectButtonTypeComponent } from './types/select-button-type/select-button-type.component';
import { SelectTypeComponent } from './types/select-type/select-type.component';
import { StepTypeComponent } from './types/step-type/step-type.component';
import { TabTypeComponent } from './types/tab-type/tab-type.component';
import { TableTypeComponent } from './types/table-type/table-type.component';
import { TextAreaTypeComponent } from './types/text-area-type/text-area-type.component';
import { TextTypeComponent } from './types/text-type/text-type.component';
import { ToggleTypeComponent } from './types/toggle-type/toggle-type.component';
import { SignaturePadTypeComponent } from './types/signature-pad-type/signature-pad-type.component';
import { FieldWrapperComponent } from './wrappers/field-wrapper/field-wrapper.component';
import { GroupWrapperComponent } from './wrappers/group-wrapper/group-wrapper.component';

@NgModule({
  exports: [FormComponent, HtmlViewerComponent, FileComponent],
  imports: [
    CommonModule,
    QuillModule.forRoot(),
    ReactiveFormsModule,
    NgxFormly,
    TranslocoModule,
    SpinnerComponent,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    FormlySelectModule,
    InputNumberModule,
    MultiSelectModule,
    AutoCompleteModule,
    TextareaModule,
    TableModule,
    TabsModule,
    BadgeModule,
    TabsQueryDirective,
    InputMaskModule,
    ColorPickerModule,
    ToggleSwitchModule,
    Select,
    DatePicker,
    InputGroup,
    InputGroupAddon,
    FormComponent,
    FieldWrapperComponent,
    TextTypeComponent,
    CheckboxTypeComponent,
    DateTypeComponent,
    SelectTypeComponent,
    NumberTypeComponent,
    GroupWrapperComponent,
    MultiSelectTypeComponent,
    RepeatTypeComponent,
    AutoCompleteTypeComponent,
    TextAreaTypeComponent,
    TableTypeComponent,
    AutoCompleteSelectTypeComponent,
    LabelTypeComponent,
    FileTypeComponent,
    TabTypeComponent,
    ToggleTypeComponent,
    InputMaskTypeComponent,
    ColorTypeComponent,
    EditorTypeComponent,
    HtmlViewerComponent,
    FileComponent,
    PickListComponent,
    PickListTypeComponent,
    SignaturePadTypeComponent,
    SelectButtonTypeComponent
  ]
})
export class FormlyModule {
  static forRoot(): ModuleWithProviders<NgxFormly> {
    return NgxFormly.forRoot({
      types: [
        { name: 'tab', component: TabTypeComponent },
        { name: 'step', component: StepTypeComponent },
        { name: 'repeat', component: RepeatTypeComponent },
        { name: 'table', component: TableTypeComponent },
        { name: 'label', component: LabelTypeComponent },
        {
          name: 'text',
          component: TextTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'number',
          component: NumberTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'select',
          component: SelectTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'select-button',
          component: SelectButtonTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'multi-select',
          component: MultiSelectTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'date-picker',
          component: DateTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'input-mask',
          component: InputMaskTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'autocomplete',
          component: AutoCompleteTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'autocomplete-select',
          component: AutoCompleteSelectTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'file',
          component: FileTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'pick-list',
          component: PickListTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'textarea',
          component: TextAreaTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'color-picker',
          component: ColorTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'checkbox',
          component: CheckboxTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'toggle',
          component: ToggleTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'editor',
          component: EditorTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'radio',
          component: RadioTypeComponent,
          wrappers: ['field-wrapper']
        },
        {
          name: 'signature-pad',
          component: SignaturePadTypeComponent,
          wrappers: ['field-wrapper']
        }
      ],
      wrappers: [
        {
          name: 'field-wrapper',
          component: FieldWrapperComponent
        },
        {
          name: 'group-wrapper',
          component: GroupWrapperComponent
        }
      ],
      validators: [{ name: 'requiredTrue', validation: Validators.requiredTrue }]
    });
  }

  static forRootProvider(): Provider[] {
    return [
      {
        provide: FORMLY_CONFIG,
        multi: true,
        useFactory: registerFormlyTranslocoExtension,
        deps: [TranslocoService]
      }
    ];
  }
}
